import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    createTask,
    getAllTaskPriority,
    getAllTaskStatus,
    getAllTaskType,
} from '../../../store/actions/picker.action';
import { getSelectList } from '../../../config/utils';
import LoaderSpinner from '../../common/Loader';
import DatePickerComponent from '../../common/DatePickerComponent';

/* istanbul ignore next */
const AddTaskModal = ({ show, handleCancel, productContext, onCreated }) => {
    const dispatch = useDispatch();
    const { allTaskPriorityList, allTaskStatusList, allTaskTypeList } = useSelector(
        (state) => state.pickerReducer || {}
    );

    const initialState = {
        taskTitle: '',
        taskTypeId: 0,
        taskPriorityId: 0,
        entryDate: new Date(),
        instructions: '',
    };

    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loader, setLoader] = useState(false);

    useEffect(() => {
        if (!show) return;
        if (!allTaskPriorityList?.length) dispatch(getAllTaskPriority());
        if (!allTaskStatusList?.length) dispatch(getAllTaskStatus());
        if (!allTaskTypeList?.length) dispatch(getAllTaskType());
    }, [show]);

    useEffect(() => {
        if (show) {
            setFormData(initialState);
            setErrors({});
        }
    }, [show]);

    const taskTypeOptions = getSelectList(allTaskTypeList || [], 'id', 'typeName');
    const taskPriorityOptions = getSelectList(allTaskPriorityList || [], 'id', 'priorityName');

    const validate = () => {
        const e = {};
        if (!formData.taskTitle?.trim()) e.taskTitle = 'Title is required';
        if (!Number(formData.taskTypeId)) e.taskTypeId = 'Task Type is required';
        if (!Number(formData.taskPriorityId)) e.taskPriorityId = 'Priority is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        const defaultStatusId =
            (allTaskStatusList || []).find((s) => /pending|open|new/i.test(s.statusName))?.id ||
            allTaskStatusList?.[0]?.id ||
            1;

        const payload = {
            taskTitle: formData.taskTitle.trim(),
            taskTypeId: Number(formData.taskTypeId),
            taskPriorityId: Number(formData.taskPriorityId),
            taskStatusId: defaultStatusId,
            instructions: formData.instructions || '',
            entryDate: formData.entryDate,
            sku: productContext?.sku || '',
            quantity: productContext?.availableQty || 0,
            amount: 0,
            companyId: productContext?.companyId || 0,
            sellerId: productContext?.sellerId || 0,
            taskForCompanyId: productContext?.companyId || 0,
            taskForSellerId: productContext?.sellerId || 0,
            inboundProductId: productContext?.inboundProductId || 0,
            taskReferenceId: productContext?.referenceId || 0,
            taskReferenceType: productContext?.referenceType || '',
            attachments: [],
            isActive: true,
        };

        try {
            setLoader(true);
            const response = await createTask(payload);
            if (response?.data?.Status === 200) {
                toast.success('Task created successfully');
                if (onCreated) onCreated(response.data.Entity);
                handleCancel();
            } else {
                toast.error(
                    response?.data?.Message?.AppStatusDescription || 'Failed to create task'
                );
            }
        } catch (err) {
            console.error(err);
            toast.error('An error occurred while creating the task.');
        } finally {
            setLoader(false);
        }
    };

    const renderHeader = () => {
        const parts = [];
        if (productContext?.sku) parts.push(`SKU: ${productContext.sku}`);
        if (productContext?.productName) parts.push(productContext.productName);
        if (productContext?.availableQty !== undefined && productContext?.availableQty !== null) {
            parts.push(`Available Qty: ${productContext.availableQty}`);
        }
        if(productContext?.productType) parts.push(`Type: ${productContext.productType}`);
        if(productContext?.trackingNumber) parts.push(`Tracking: ${productContext.trackingNumber}`);
        return parts.join('  \u00B7  ');
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static" size="lg">
            <Modal.Header>
                <div>
                    <Modal.Title>Add Task</Modal.Title>
                    {renderHeader() && (
                        <div className="text-muted small mt-1">{renderHeader()}</div>
                    )}
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    className="btn btn-link p-0 border-0"
                    style={{ position: 'absolute', top: 12, right: 16, fontSize: 20, color: '#6c757d' }}
                    onClick={handleCancel}
                >
                    <i className="fa fa-times" />
                </button>
            </Modal.Header>
            <Modal.Body>
                {loader && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}
                <Row>
                    <Col xs={12} className="mb-3">
                        <div className="input-label-txt">
                            Title <span className="require-field">*</span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="e.g. Inspect and label returned units for resale"
                            value={formData.taskTitle}
                            onChange={(e) =>
                                setFormData({ ...formData, taskTitle: e.target.value })
                            }
                        />
                        {errors.taskTitle && (
                            <span className="error-message">{errors.taskTitle}</span>
                        )}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Task Type <span className="require-field">*</span>
                        </div>
                        <Select
                            options={taskTypeOptions}
                            value={
                                taskTypeOptions.find(
                                    (o) => o.value === Number(formData.taskTypeId)
                                ) || null
                            }
                            onChange={(opt) =>
                                setFormData({ ...formData, taskTypeId: opt?.value || 0 })
                            }
                            placeholder="Select Type"
                            isClearable
                            menuPlacement="auto"
                            maxMenuHeight={180}
                        />
                        {errors.taskTypeId && (
                            <span className="error-message">{errors.taskTypeId}</span>
                        )}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Priority <span className="require-field">*</span>
                        </div>
                        <Select
                            options={taskPriorityOptions}
                            value={
                                taskPriorityOptions.find(
                                    (o) => o.value === Number(formData.taskPriorityId)
                                ) || null
                            }
                            onChange={(opt) =>
                                setFormData({ ...formData, taskPriorityId: opt?.value || 0 })
                            }
                            placeholder="Select Priority"
                            isClearable
                            menuPlacement="auto"
                            maxMenuHeight={180}
                        />
                        {errors.taskPriorityId && (
                            <span className="error-message">{errors.taskPriorityId}</span>
                        )}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">Date</div>
                        <DatePickerComponent
                            dateField={formData.entryDate}
                            handleDateChange={(d) => setFormData({ ...formData, entryDate: d })}
                            fieldName="entryDate"
                            dateFormat="MM-dd-yyyy"
                        />
                    </Col>
                    <Col xs={12} className="mb-3">
                        <div className="input-label-txt">
                            Comment / Instructions for warehouse team
                        </div>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Add any instructions..."
                            value={formData.instructions}
                            onChange={(e) =>
                                setFormData({ ...formData, instructions: e.target.value })
                            }
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn cus-seconday-bg-btn"
                    disabled={loader}
                    onClick={handleSubmit}
                >
                    Create Task
                </button>
                <button
                    type="button"
                    className="btn cus-primary-transparent-btn"
                    disabled={loader}
                    onClick={handleCancel}
                >
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddTaskModal;

AddTaskModal.propTypes = {
    show: PropTypes.bool,
    handleCancel: PropTypes.func,
    productContext: PropTypes.object,
    onCreated: PropTypes.func,
};
